/**
 * IpawsConfigContent — Account → Integrations → IPAWS sub-tab body.
 *
 * District-level IPAWS-OPEN origination config per ADR-139 D4: one config row
 * per organization — a COG (Collaborative Operations Group) belongs to the
 * alerting authority (the district), never to an individual school. Backed by
 * `/api/v2/ipaws-config/`.
 *
 * Two load-bearing invariants surfaced in the UX:
 *   - This config MIRRORS the FEMA COG profile. Channel/event-code enablement
 *     here is a UX convenience; IPAWS-OPEN enforces the real authorizations
 *     server-side, and `getCOG` output is the reconciliation source when they
 *     disagree (ADR-139 D4).
 *   - The certificate field stores an AWS Secrets Manager secret NAME
 *     (`ipaws/cog/<cogId>`) — never certificate/key material. Ops places the
 *     actual certificate per the COG cert rotation runbook
 *     (doc/adr/runbooks/139-ipaws-cog-cert-rotation-runbook.md). Both the API
 *     and the client-side schema reject pasted PEM material.
 *
 * Visibility is gated on the `schools.view_ipawsconfig` model permission
 * (writes on add/change) — the same perms the viewset enforces. Those are
 * ADR-123 disposition B: granted to no built-in role, composed per admin.
 *
 * Test-connection (announce testConnection → IPAWS-OPEN `getAck`) is wired
 * to `POST /api/v2/ipaws-config/test-connection/`,
 * gated on `schools.change_ipawsconfig` — the same perm the endpoint maps
 * POST to (probing OPERATES the org's live COG credential). The transport is
 * fire-and-forget (EventBridge), so a 202 only means "probe dispatched" —
 * the toast says exactly that and never fabricates a probe result.
 *
 * The actual getAck outcome IS shown here: the ipaws-announce
 * provider reports it back and the backend persists it, read via
 * `GET /api/v2/ipaws-config/last-probe/` (same `view_ipawsconfig` surface,
 * org-scoped, `?district=` disambiguation). Because the report-back is
 * asynchronous, a successful dispatch opens a short poll window
 * (PROBE_POLL_WINDOW_MS, 5s ticks via react-query `refetchInterval`) so the
 * outcome appears without a manual reload; outside the window the read is
 * one-shot.
 *
 * The geo-targeting review section reads the per-school
 * fields off the site API (`same_geocodes` + `cap_polygon`, derived and
 * stored by `manage.py backfill_ipaws_geo`). Derivation failures are NOT
 * persisted — the backfill command reports them — so on this page the
 * absence of both fields IS the failed/missing state ("Not derived").
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Center,
  Field,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { HiOutlineTrash, HiPlus, HiSignal } from 'react-icons/hi2';
import {
  Button as ButtonComponent,
  DataTable,
  EmptyState,
  InputField,
  SectionCard,
  SelectInput,
  StatusBadge,
  ToggleButton,
  Tooltip,
  type Column,
} from '@themes/aegix';
import {
  IPAWS_CHANNELS,
  IPAWS_CHANNEL_LABELS,
  type IpawsChannel,
  type IpawsConfig,
  type IpawsConfigWrite,
  type IpawsProbeOutcome,
} from '../../services/ipaws.service';
import type { Site } from '../../services/sites.service';
import {
  useCreateIpawsConfig,
  useIpawsConfigs,
  useIpawsLastProbe,
  useTestIpawsConnection,
  useUpdateIpawsConfig,
} from '../../hooks/queries/useIpawsConfig';
import { useSites } from '../../hooks/queries/useSites';
import { useHasPermissions } from '../../utils/hasPermission';
import {
  eventCodeMapToRows,
  eventCodeRowsToMap,
  ipawsConfigFormSchema,
  type IpawsConfigFormValues,
} from '../../validations/ipaws.validation';
import { toaster } from '../ui/toaster';

/** Readiness of a site's CAP `<area>` inputs — both pieces stored, or one. */
export type GeoReadiness = 'ready' | 'partial';

/** Per-school geo-review row (CAP `<area>` inputs). */
export interface SiteGeoReview {
  geocodes: string[];
  hasPolygon: boolean;
  readiness: GeoReadiness;
}

/**
 * Per-school SAME/FIPS geocodes + boundary polygon (the CAP `<area>` inputs),
 * read off the site fields (`same_geocodes` + `cap_polygon`). Both are
 * derived and stored by `manage.py backfill_ipaws_geo`;
 * derivation failures are reported by that command, not persisted, so a site
 * with NEITHER field stored returns null here — the failed/missing state the
 * table flags as "Not derived". One piece present → 'partial'; both → 'ready'.
 */
export function getSiteGeoReview(site: Site): SiteGeoReview | null {
  const geocodes = Array.isArray(site.same_geocodes)
    ? site.same_geocodes.filter(
        (code): code is string => typeof code === 'string' && code.trim() !== '',
      )
    : [];
  const hasPolygon =
    typeof site.cap_polygon === 'string' && site.cap_polygon.trim() !== '';
  if (geocodes.length === 0 && !hasPolygon) {
    return null;
  }
  return {
    geocodes,
    hasPolygon,
    readiness: geocodes.length > 0 && hasPolygon ? 'ready' : 'partial',
  };
}

/** Readiness flag for a geo-review row — "Not derived" is the failure state. */
function GeoReadinessBadge({ review }: { review: SiteGeoReview | null }) {
  if (!review) {
    return (
      <Box data-e2e="ipaws-geo-readiness-missing">
        <StatusBadge status="error" label="Not derived" />
      </Box>
    );
  }
  if (review.readiness === 'ready') {
    return (
      <Box data-e2e="ipaws-geo-readiness-ready">
        <StatusBadge status="success" label="Ready" />
      </Box>
    );
  }
  return (
    <Box data-e2e="ipaws-geo-readiness-partial">
      <StatusBadge status="warning" label="Partial" />
    </Box>
  );
}

/**
 * How long the page keeps polling the last-probe endpoint after a successful
 * dispatch. The provider's getAck report-back usually lands within
 * seconds; ~30s comfortably covers the announce fan-out without leaving a
 * poll running forever on an abandoned tab.
 */
export const PROBE_POLL_WINDOW_MS = 30_000;

/**
 * Last probe outcome (the `last-probe` read surface). Exactly three render
 * states per the persisted contract: no outcome yet (never probed, or the
 * report-back hasn't landed), success + timestamp, failure + reason +
 * timestamp. `probed_at` is provider-reported — when the outcome was
 * OBSERVED, not when the row was written.
 */
function LastProbeOutcome({ outcome }: { outcome: IpawsProbeOutcome | null }) {
  if (!outcome) {
    return (
      <Text fontSize="sm" color="text.tertiary" data-e2e="ipaws-last-probe-empty">
        No probe outcome recorded yet.
      </Text>
    );
  }
  const succeeded = outcome.status === 'success';
  return (
    <VStack align="stretch" gap={1}>
      <Flex align="center" gap={3} wrap="wrap">
        <Box
          data-e2e={
            succeeded ? 'ipaws-last-probe-success' : 'ipaws-last-probe-error'
          }
        >
          <StatusBadge
            status={succeeded ? 'success' : 'error'}
            label={succeeded ? 'Success' : 'Failed'}
          />
        </Box>
        <Text
          fontSize="sm"
          color="text.secondary"
          data-e2e="ipaws-last-probe-timestamp"
        >
          {new Date(outcome.probed_at).toLocaleString()}
        </Text>
      </Flex>
      {!succeeded && outcome.message && (
        <Text
          fontSize="sm"
          color="text.secondary"
          data-e2e="ipaws-last-probe-message"
        >
          {outcome.message}
        </Text>
      )}
    </VStack>
  );
}

function toFormValues(config: IpawsConfig | null): IpawsConfigFormValues {
  return {
    is_enabled: config?.is_enabled ?? false,
    cog_id: config?.cog_id ?? '',
    certificate_secret_name: config?.certificate_secret_name ?? '',
    enabled_channels: config?.enabled_channels ?? [],
    event_code_rows: eventCodeMapToRows(config?.default_event_codes),
  };
}

/** Best-effort human message out of a failed save (ky HTTPError or Error). */
function saveErrorMessage(err: unknown): string | undefined {
  if (err instanceof Error && err.message) return err.message;
  return undefined;
}

/**
 * Human message out of a failed probe dispatch. The test-connection endpoint
 * answers every non-2xx with a truthful `{ dispatched: false, detail }` body
 * (400 gated / 403 forbidden / 502 publish failure) — surface that `detail`
 * when present, falling back to the generic error message. Structural
 * `.response` check rather than `instanceof HTTPError` — same convention as
 * sentrySetup (ADR 113: no direct ky dependency in the app).
 */
async function probeErrorMessage(err: unknown): Promise<string | undefined> {
  const response = (err as { response?: Response } | null)?.response;
  if (response && typeof response.clone === 'function') {
    try {
      const body = (await response.clone().json()) as { detail?: unknown };
      if (typeof body.detail === 'string' && body.detail) return body.detail;
    } catch {
      // Non-JSON error body — fall through to the generic message.
    }
  }
  return saveErrorMessage(err);
}

export function IpawsConfigContent() {
  const canView = useHasPermissions(['schools.view_ipawsconfig']);
  const canAdd = useHasPermissions(['schools.add_ipawsconfig']);
  const canChange = useHasPermissions(['schools.change_ipawsconfig']);

  const configsQuery = useIpawsConfigs(canView);
  const sitesQuery = useSites({ limit: 200 });

  // Districts are derived from the sites themselves (`site.district` /
  // `site.district_name`) rather than `useActiveOrganization`, whose id is
  // school-shaped in places (same gotcha as whakahaere's useScopeRows).
  const districts = useMemo(() => {
    const byId = new Map<number, string>();
    for (const site of sitesQuery.data?.results ?? []) {
      if (site.district != null && !byId.has(site.district)) {
        byId.set(site.district, site.district_name ?? 'Organization');
      }
    }
    return [...byId.entries()].map(([id, name]) => ({ id, name }));
  }, [sitesQuery.data]);

  const configs = useMemo(() => configsQuery.data ?? [], [configsQuery.data]);

  // Target district: an existing config row wins; otherwise auto-select when
  // there is no real choice; otherwise the admin picks explicitly.
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(
    null,
  );
  useEffect(() => {
    if (selectedDistrictId != null) return;
    if (configsQuery.isLoading || sitesQuery.isLoading) return;
    if (configs.length > 0) {
      setSelectedDistrictId(configs[0].district);
    } else if (districts.length === 1) {
      setSelectedDistrictId(districts[0].id);
    }
  }, [
    selectedDistrictId,
    configs,
    districts,
    configsQuery.isLoading,
    sitesQuery.isLoading,
  ]);

  const config = useMemo(
    () =>
      configs.find((c) => c.district === selectedDistrictId) ?? null,
    [configs, selectedDistrictId],
  );

  const canEdit = config ? canChange : canAdd;

  const createConfig = useCreateIpawsConfig();
  const updateConfig = useUpdateIpawsConfig();
  const testConnection = useTestIpawsConnection();

  // Last probe outcome: read once on load, then poll for a short
  // window after each dispatch — the getAck report-back is asynchronous.
  // `probePollUntil` is an epoch-ms deadline; the effect below clears it so
  // the "checking…" hint drops off when the window lapses.
  const [probePollUntil, setProbePollUntil] = useState<number | null>(null);
  const lastProbeQuery = useIpawsLastProbe(selectedDistrictId, {
    enabled: canView,
    pollUntil: probePollUntil,
  });
  const lastProbe = lastProbeQuery.data?.last_probe ?? null;

  useEffect(() => {
    if (probePollUntil == null) return undefined;
    const remaining = probePollUntil - Date.now();
    if (remaining <= 0) {
      setProbePollUntil(null);
      return undefined;
    }
    const timer = setTimeout(() => setProbePollUntil(null), remaining);
    return () => clearTimeout(timer);
  }, [probePollUntil]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IpawsConfigFormValues>({
    defaultValues: toFormValues(config),
    resolver: zodResolver(ipawsConfigFormSchema),
    mode: 'onBlur',
  });

  const eventCodeRows = useFieldArray({ control, name: 'event_code_rows' });

  useEffect(() => {
    if (!configsQuery.isLoading) {
      reset(toFormValues(config));
    }
  }, [config, configsQuery.isLoading, reset]);

  const onSubmit = async (values: IpawsConfigFormValues) => {
    if (selectedDistrictId == null) {
      toaster.create({
        title: 'Select an organization first',
        type: 'error',
      });
      return;
    }
    const payload: IpawsConfigWrite = {
      district: selectedDistrictId,
      is_enabled: values.is_enabled,
      cog_id: values.cog_id,
      certificate_secret_name: values.certificate_secret_name,
      enabled_channels: values.enabled_channels,
      default_event_codes: eventCodeRowsToMap(values.event_code_rows),
    };
    try {
      if (config) {
        await updateConfig.mutateAsync({ id: config.id, data: payload });
        toaster.create({ title: 'IPAWS configuration updated', type: 'success' });
      } else {
        await createConfig.mutateAsync(payload);
        toaster.create({ title: 'IPAWS configuration created', type: 'success' });
      }
    } catch (err) {
      toaster.create({
        title: 'Could not save the IPAWS configuration',
        description: saveErrorMessage(err),
        type: 'error',
      });
    }
  };

  /**
   * Dispatch the announce testConnection probe. The
   * district rides along explicitly — staff callers who can see multiple
   * orgs' configs would otherwise get a 400 asking to disambiguate. Both
   * toasts stay honest about the fire-and-forget transport: 202 means the
   * probe was DISPATCHED; the getAck outcome lands asynchronously on the
   * last-probe surface, so a success opens the poll window.
   */
  const onTestConnection = async () => {
    if (!config) return;
    try {
      await testConnection.mutateAsync(config.district);
      setProbePollUntil(Date.now() + PROBE_POLL_WINDOW_MS);
      toaster.create({
        title: 'IPAWS connectivity probe dispatched',
        description:
          'Dispatch is fire-and-forget — the getAck outcome appears under ' +
          '"Last probe outcome" on this page once the provider reports back, ' +
          'usually within a few seconds.',
        type: 'success',
      });
    } catch (err) {
      toaster.create({
        title: 'Could not dispatch the IPAWS connectivity probe',
        description: await probeErrorMessage(err),
        type: 'error',
      });
    }
  };

  // Probing operates the saved config against the org's live COG credential,
  // so the gate mirrors the endpoint's: change_ipawsconfig + a saved row.
  const testDisabledReason = !canChange
    ? 'You need the IPAWS change permission to test the connection.'
    : !config
      ? 'Save the IPAWS configuration first — the probe runs against the saved configuration.'
      : null;

  const sites = sitesQuery.data?.results ?? [];
  const geoRows = useMemo(
    () =>
      sites.map((site) => ({
        site,
        review: getSiteGeoReview(site),
      })),
    [sites],
  );
  const hasAnyGeoData = geoRows.some((row) => row.review != null);

  const geoColumns: Column<(typeof geoRows)[number]>[] = [
    {
      key: 'site',
      label: 'Site',
      render: (row) => <Text fontSize="sm">{row.site.name}</Text>,
    },
    {
      key: 'geocodes',
      label: 'SAME geocodes',
      render: (row) => (
        <Text fontSize="sm" color="text.secondary">
          {row.review && row.review.geocodes.length > 0
            ? row.review.geocodes.join(', ')
            : '—'}
        </Text>
      ),
    },
    {
      key: 'polygon',
      label: 'Boundary polygon',
      render: (row) => (
        <Text fontSize="sm" color="text.secondary">
          {row.review ? (row.review.hasPolygon ? 'Present' : 'None') : '—'}
        </Text>
      ),
    },
    {
      key: 'readiness',
      label: 'Readiness',
      render: (row) => <GeoReadinessBadge review={row.review} />,
    },
  ];

  if (!canView) {
    return (
      <Box px={8} py={8}>
        <EmptyState
          title="IPAWS configuration is restricted"
          description="You need the IPAWS configuration permission to view this page. Ask an administrator to grant access."
          e2eTitle="ipaws-no-permission"
        />
      </Box>
    );
  }

  if (configsQuery.isLoading || sitesQuery.isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  return (
    <Box px={8} pb={8} pt={5} maxW="4xl" data-e2e="ipaws-config-content">
      <VStack align="stretch" gap={5}>
        <Box>
          <Text fontWeight="600" mb={1}>
            IPAWS origination
          </Text>
          <Text fontSize="sm" color="text.secondary">
            Configuration for originating alerts into FEMA&apos;s Integrated
            Public Alert and Warning System (IPAWS-OPEN). One configuration per
            organization — the COG belongs to the alerting authority, not to an
            individual site. Settings here mirror the FEMA COG profile; they
            never override it.
          </Text>
        </Box>

        {districts.length > 1 && !config && (
          <Box maxW="sm">
            <SelectInput
              label="Organization"
              name="ipaws-district"
              placeholder="Select an organization…"
              value={selectedDistrictId != null ? String(selectedDistrictId) : ''}
              option={districts.map((d) => ({
                label: d.name,
                value: String(d.id),
              }))}
              onChange={(val: string) => setSelectedDistrictId(Number(val))}
              e2eTitle="ipaws-district-select"
            />
          </Box>
        )}

        <form onSubmit={handleSubmit(onSubmit)} data-e2e="ipaws-config-form">
          <VStack align="stretch" gap={5}>
            <SectionCard title="COG identity" e2eTitle="ipaws-cog-card">
              <VStack align="stretch" gap={4} px={5} py={5}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  <Field.Root invalid={!!errors.cog_id}>
                    <Field.Label htmlFor="ipaws-cog-id">COG ID</Field.Label>
                    <Controller
                      name="cog_id"
                      control={control}
                      render={({ field }) => (
                        <InputField
                          id="ipaws-cog-id"
                          placeholder="e.g. 120123"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          disabled={!canEdit}
                          data-e2e="ipaws-cog-id-input"
                        />
                      )}
                    />
                    <Field.HelperText>
                      FEMA-assigned Collaborative Operations Group identifier.
                    </Field.HelperText>
                    {errors.cog_id && (
                      <Field.ErrorText>{errors.cog_id.message}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root invalid={!!errors.certificate_secret_name}>
                    <Field.Label htmlFor="ipaws-cert-secret">
                      Certificate secret name
                    </Field.Label>
                    <Controller
                      name="certificate_secret_name"
                      control={control}
                      render={({ field }) => (
                        <InputField
                          id="ipaws-cert-secret"
                          placeholder="ipaws/cog/<cogId>"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          disabled={!canEdit}
                          data-e2e="ipaws-cert-secret-input"
                        />
                      )}
                    />
                    <Field.HelperText>
                      AWS Secrets Manager secret NAME holding the COG
                      certificate (e.g. ipaws/cog/120123). Operations places the
                      actual certificate per the COG certificate rotation
                      runbook — never paste certificate or key material here.
                    </Field.HelperText>
                    {errors.certificate_secret_name && (
                      <Field.ErrorText>
                        {errors.certificate_secret_name.message}
                      </Field.ErrorText>
                    )}
                  </Field.Root>
                </SimpleGrid>

                <Box maxW="md">
                  <Controller
                    name="is_enabled"
                    control={control}
                    render={({ field }) => (
                      <ToggleButton
                        title="Enable IPAWS origination"
                        checked={field.value}
                        onCheckedChange={(details: { checked: boolean }) =>
                          field.onChange(details.checked)
                        }
                        disabled={!canEdit}
                        e2eTitle="ipaws-is-enabled-toggle"
                      />
                    )}
                  />
                  <Text fontSize="xs" color="text.tertiary" mt={1}>
                    Master arm switch — off keeps origination dark while the
                    configuration is prepared. Alert starts are never blocked by
                    a disabled config; the IPAWS flag simply does not propagate.
                  </Text>
                </Box>
              </VStack>
            </SectionCard>

            <SectionCard title="Channels" e2eTitle="ipaws-channels-card">
              <VStack align="stretch" gap={4} px={5} py={5}>
                <Text fontSize="sm" color="text.secondary">
                  Channels take effect only as FEMA authorizes them for this COG.
                  This selection mirrors — never overrides — the FEMA COG
                  profile; IPAWS-OPEN enforces the real authorizations
                  server-side.
                </Text>
                <Controller
                  name="enabled_channels"
                  control={control}
                  render={({ field }) => (
                    <VStack align="stretch" gap={2} maxW="md">
                      {IPAWS_CHANNELS.map((channel: IpawsChannel) => (
                        <ToggleButton
                          key={channel}
                          title={IPAWS_CHANNEL_LABELS[channel]}
                          checked={field.value.includes(channel)}
                          onCheckedChange={(details: { checked: boolean }) =>
                            field.onChange(
                              details.checked
                                ? [...field.value, channel]
                                : field.value.filter(
                                    (c: IpawsChannel) => c !== channel,
                                  ),
                            )
                          }
                          disabled={!canEdit}
                          e2eTitle={`ipaws-channel-${channel}`}
                        />
                      ))}
                    </VStack>
                  )}
                />
              </VStack>
            </SectionCard>

            <SectionCard
              title="Default SAME event codes"
              e2eTitle="ipaws-event-codes-card"
              action={
                canEdit ? (
                  <ButtonComponent
                    visual="secondary"
                    type="button"
                    onClick={() =>
                      eventCodeRows.append({ alertType: '', eventCode: '' })
                    }
                    data-e2e="ipaws-add-event-code"
                  >
                    <HiPlus /> Add mapping
                  </ButtonComponent>
                ) : undefined
              }
            >
              <VStack align="stretch" gap={4} px={5} py={5}>
                <Text fontSize="sm" color="text.secondary">
                  Default SAME event code per alert-type name (e.g. Fire → FRW,
                  Intruder → CEM). Used when an IPAWS-flagged alert type has no
                  explicit code.
                </Text>
                {eventCodeRows.fields.length === 0 ? (
                  <Text fontSize="sm" color="text.tertiary" data-e2e="ipaws-event-codes-empty">
                    No mappings configured.
                  </Text>
                ) : (
                  eventCodeRows.fields.map((row, index) => (
                    <Flex key={row.id} gap={3} align="flex-start">
                      <Field.Root
                        flex={2}
                        invalid={!!errors.event_code_rows?.[index]?.alertType}
                      >
                        <Controller
                          name={`event_code_rows.${index}.alertType`}
                          control={control}
                          render={({ field }) => (
                            <InputField
                              aria-label={`Alert-type name — code mapping ${index + 1}`}
                              placeholder="Alert-type name (e.g. Fire)"
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              disabled={!canEdit}
                              data-e2e={`ipaws-event-code-name-${index}`}
                            />
                          )}
                        />
                        {errors.event_code_rows?.[index]?.alertType && (
                          <Field.ErrorText>
                            {errors.event_code_rows[index]?.alertType?.message}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                      <Field.Root
                        flex={1}
                        invalid={!!errors.event_code_rows?.[index]?.eventCode}
                      >
                        <Controller
                          name={`event_code_rows.${index}.eventCode`}
                          control={control}
                          render={({ field }) => (
                            <InputField
                              aria-label={`IPAWS event code — code mapping ${index + 1}`}
                              placeholder="Code (e.g. FRW)"
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              disabled={!canEdit}
                              data-e2e={`ipaws-event-code-code-${index}`}
                            />
                          )}
                        />
                        {errors.event_code_rows?.[index]?.eventCode && (
                          <Field.ErrorText>
                            {errors.event_code_rows[index]?.eventCode?.message}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                      {canEdit && (
                        <ButtonComponent
                          visual="secondary"
                          type="button"
                          onClick={() => eventCodeRows.remove(index)}
                          aria-label="Remove mapping"
                          data-e2e={`ipaws-event-code-remove-${index}`}
                        >
                          <HiOutlineTrash />
                        </ButtonComponent>
                      )}
                    </Flex>
                  ))
                )}
              </VStack>
            </SectionCard>

            <Flex justify="flex-end">
              <ButtonComponent
                visual="primary"
                type="submit"
                disabled={
                  !canEdit || isSubmitting || selectedDistrictId == null
                }
                data-e2e="ipaws-save-button"
              >
                {config ? 'Save changes' : 'Create configuration'}
              </ButtonComponent>
            </Flex>
          </VStack>
        </form>

        {/* Enabled against POST /api/v2/ipaws-config/test-connection/.
            The e2e contract (click → test-connection POST →
            "probe dispatched" toast, plus the unconfigured/permission-gated
            disabled variants) lives in aegix-sms-react-e2e's
            ipawsConfigValidation.cy.ts. */}
        <SectionCard
          title="Connection"
          e2eTitle="ipaws-connection-card"
          action={
            testDisabledReason ? (
              <Tooltip
                content={testDisabledReason}
                positioning={{ placement: 'top-end' }}
              >
                {/* Disabled buttons swallow pointer events; the Box keeps the
                    tooltip trigger hoverable. */}
                <Box display="inline-block">
                  <ButtonComponent
                    visual="secondary"
                    type="button"
                    disabled
                    data-e2e="ipaws-test-connection-button"
                  >
                    <HiSignal /> Test connection
                  </ButtonComponent>
                </Box>
              </Tooltip>
            ) : (
              <ButtonComponent
                visual="secondary"
                type="button"
                onClick={onTestConnection}
                disabled={testConnection.isPending}
                data-e2e="ipaws-test-connection-button"
              >
                <HiSignal />{' '}
                {testConnection.isPending ? 'Dispatching…' : 'Test connection'}
              </ButtonComponent>
            )
          }
        >
          <VStack align="stretch" gap={4} px={5} py={5}>
            <Text fontSize="sm" color="text.secondary">
              Runs an IPAWS-OPEN getAck probe — a side-effect-free connectivity
              and authentication check against FEMA using this COG&apos;s
              certificate. Dispatch is fire-and-forget: the provider reports
              the outcome back asynchronously, and the latest result appears
              below.
            </Text>
            <Box data-e2e="ipaws-last-probe">
              <Text fontSize="sm" fontWeight="600" mb={1}>
                Last probe outcome
              </Text>
              {lastProbeQuery.isLoading ? (
                <Spinner size="sm" color="button.primary" />
              ) : (
                <LastProbeOutcome outcome={lastProbe} />
              )}
              {probePollUntil != null && (
                <Text
                  fontSize="xs"
                  color="text.tertiary"
                  mt={1}
                  data-e2e="ipaws-last-probe-polling"
                >
                  Probe dispatched — checking for the outcome…
                </Text>
              )}
            </Box>
          </VStack>
        </SectionCard>

        <SectionCard
          title="Per-site geo targeting review"
          e2eTitle="ipaws-geo-review-card"
        >
          <Box px={5} py={5}>
            <Text fontSize="sm" color="text.secondary" mb={4}>
              Read-only review of each site&apos;s SAME/FIPS geocodes and
              boundary polygon — these feed the CAP area block on an IPAWS
              submission. Sites flagged &quot;Not derived&quot; have no stored
              geo data: derivation is managed by the geo-targeting backfill,
              which reports per-site failures when it runs.
            </Text>
            {hasAnyGeoData ? (
              <DataTable
                columns={geoColumns}
                data={geoRows}
                keyExtractor={(row) => String(row.site.id)}
              />
            ) : (
              <EmptyState
                title="No SAME geocodes configured yet"
                description="No site in this organization has derived geo-targeting data yet. Per-site SAME/FIPS geocodes and boundary polygons are derived and stored by the geo-targeting backfill (AAS-1462) and appear here for review once it has run."
                e2eTitle="ipaws-geo-review-empty"
              />
            )}
          </Box>
        </SectionCard>
      </VStack>
    </Box>
  );
}
